import React, { useState, useEffect } from 'react';
import ScreenshotModal from './ScreenshotModal';
import LighthouseModal from './LighthouseModal';
import NetworkDetailsModal from './NetworkDetailsModal';
import PerformanceDetailsModal from './PerformanceDetailsModal';
import ConsoleLogsModal from './ConsoleLogsModal';
import WebsiteGraphModal from './WebsiteGraphModal';
import ImagesModal from './ImagesModal';
import VideosModal from './VideosModal';
import FilesModal from './FilesModal';
import FormDetailsModal from './FormDetailsModal';
import WebsiteAuditLogsModal from './WebsiteAuditLogsModal';

// Hooks
import { useWebsiteData } from '../hooks/useWebsiteData';
import { useLighthouseReport } from '../hooks/useLighthouseReport';
import { useCoreWebVitals } from '../hooks/useCoreWebVitals';
import { useWebsiteAuditLogs } from '../hooks/useWebsiteAuditLogs';

// Section Components
import WebsiteHeader from './sections/WebsiteHeader';
import EvaluationSection from './sections/EvaluationSection';
import WebsiteEvaluationSection from './sections/WebsiteEvaluationSection';
import GatekeeperSection from './sections/GatekeeperSection';
import AnswersSection from './sections/AnswersSection';
import CustomerFitSection from './sections/CustomerFitSection';
import SiteTypeSection from './sections/SiteTypeSection';
import SEOSection from './sections/SEOSection';
import DonationsSection from './sections/DonationsSection';
import ImagesSection from './sections/ImagesSection';
import VideosSection from './sections/VideosSection';
import FilesSection from './sections/FilesSection';
import DNSSection from './sections/DNSSection';
import TrackingSection from './sections/TrackingSection';
import NetworkSummarySection from './sections/NetworkSummarySection';
import PerformanceMetricsSection from './sections/PerformanceMetricsSection';
import LighthouseSection from './sections/LighthouseSection';
import ConsoleLogsSection from './sections/ConsoleLogsSection';
import LongTextAnswersSection from './sections/LongTextAnswersSection';
import CSSSection from './sections/CSSSection';
import ReasonsSection from './sections/ReasonsSection';
import CrawlStatisticsSection from './sections/CrawlStatisticsSection';
import ContactsSection from './sections/ContactsSection';
import ContactEvaluationSection from './sections/ContactEvaluationSection';
import ProspectEvaluationSection from './sections/ProspectEvaluationSection';
import ProspectEvaluationSummary from './sections/ProspectEvaluationSummary';
import QualifiedEvaluationSection from './sections/QualifiedEvaluationSection';
import QualifiedEvaluationSummary from './sections/QualifiedEvaluationSummary';
import ContactFormUrlsSection from './sections/ContactFormUrlsSection';
import BlogUrlsSection from './sections/BlogUrlsSection';
import ProductGalleryUrlsSection from './sections/ProductGalleryUrlsSection';
import GoogleMapsUrlsSection from './sections/GoogleMapsUrlsSection';
import ThirdPartyIntegrationsUrlsSection from './sections/ThirdPartyIntegrationsUrlsSection';
import WebsiteMoodboardModal from './WebsiteMoodboardModal';
import WebsiteSalesPitchModal from './WebsiteSalesPitchModal';
import PagesSection from './sections/PagesSection';
import FormsSection from './sections/FormsSection';
import SocialMediaSection from './sections/SocialMediaSection';
import ProposedStructureSection from './sections/ProposedStructureSection';
import NavigationStructureSection from './sections/NavigationStructureSection';
import ManualActionsSection from './sections/ManualActionsSection';
import ConceptSection from './sections/ConceptSection';
import DesignSemanticSection from './sections/DesignSemanticSection';
import PipelineProgressSection from './sections/PipelineProgressSection';
import ContactsModal from './ContactsModal';
import AddContactModal from './AddContactModal';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import ClientProvider from '../../../Various/ClientProvider';

// Utils
import { formatValue, formatFieldName, formatDurationMs, formatConfidence } from '../utils/formatters';
import { FUNNEL_STATUS_LABELS, FUNNEL_STATUS_COLORS, PIPELINE_ORDER } from '../utils/funnelStatusLabels';

const WebsiteDetails = ({ websiteDetails, websiteScreenshot, websiteMobileScreenshot, selectedWebsiteHash, onPromoteWebsite, onReloadWebsite }) => {
    const [expandedScreenshotType, setExpandedScreenshotType] = useState(null);
    const [isLighthouseModalOpen, setIsLighthouseModalOpen] = useState(false);
    const [isNetworkModalOpen, setIsNetworkModalOpen] = useState(false);
    const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
    const [isConsoleLogsModalOpen, setIsConsoleLogsModalOpen] = useState(false);
    const [isWebsiteGraphModalOpen, setIsWebsiteGraphModalOpen] = useState(false);
    const [isContactsModalOpen, setIsContactsModalOpen] = useState(false);
    const [isImagesModalOpen, setIsImagesModalOpen] = useState(false);
    const [filteredImagesForModal, setFilteredImagesForModal] = useState(null);
    const [isVideosModalOpen, setIsVideosModalOpen] = useState(false);
    const [filteredVideosForModal, setFilteredVideosForModal] = useState(null);
    const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
    const [filteredFilesForModal, setFilteredFilesForModal] = useState(null);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [selectedForm, setSelectedForm] = useState(null);
    const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
    const [isWebsiteAuditLogsModalOpen, setIsWebsiteAuditLogsModalOpen] = useState(false);
    const [isMoodboardModalOpen, setIsMoodboardModalOpen] = useState(false);
    const [isMoodboardLoading, setIsMoodboardLoading] = useState(false);
    const [moodboardHtml, setMoodboardHtml] = useState('');
    const [moodboardError, setMoodboardError] = useState('');
    const [isSalesPitchModalOpen, setIsSalesPitchModalOpen] = useState(false);
    const [isSalesPitchLoading, setIsSalesPitchLoading] = useState(false);
    const [salesPitchHtml, setSalesPitchHtml] = useState('');
    const [salesPitchRpcPitchContent, setSalesPitchRpcPitchContent] = useState(null);
    const [salesPitchError, setSalesPitchError] = useState('');

    const wsProvider = React.useContext(ClientProvider);
    const websocketClient = wsProvider?.ws;

    // Use custom hooks
    const websiteData = useWebsiteData(websiteDetails);
    const {
        lighthouseReport,
        isReportLoading,
        lighthouseReportHtml,
        lighthouseReportError
    } = useLighthouseReport(
        websiteData.website?.lighthouse_report,
        isLighthouseModalOpen
    );
    const { coreWebVitalsSummary, previewCoreWebVitals } = useCoreWebVitals(
        websiteData.coreWebVitals
    );
    const { logs: auditLogs, isLoading: auditLogsLoading } = useWebsiteAuditLogs(
        selectedWebsiteHash,
        isWebsiteAuditLogsModalOpen
    );

    // Filter reasons by category for each tab
    const filteredReasons = React.useMemo(() => {
        if (!websiteData.reasons || Object.keys(websiteData.reasons).length === 0) {
            return {
                overview: {},
                customerFit: {},
                siteType: {},
                seo: {},
                performance: {},
                urls: {},
                technical: {},
                other: {},
                gatekeeper: {}
            };
        }

        const reasons = websiteData.reasons;
        const allKeys = new Set(Object.keys(reasons));
        
        // Define reason mappings
        const overviewKeys = ['looks_good', 'has_shop', 'is_non_profit', 'age_years', 'responsive'];
        const customerFitKeys = ['customer_fit'];
        const siteTypeKeys = ['site_type'];
        const seoKeys = ['seo_surface_rating'];
        const performanceKeys = ['performance_rating'];
        const urlsKeys = ['has_contact_form', 'has_blog', 'has_product_gallery', 'has_google_maps', 'third_party_integrations'];
        const technicalKeys = ['accessible', 'platform', 'technology_stack', 'technology_modernity_rating', 'mobile_readiness', 'dns_hosting_provider', 'tracking'];
        const otherKeys = ['has_donation_option'];
        const gatekeeperKeys = ['gatekeeper'];

        const filterReasons = (keys) => {
            const filtered = {};
            keys.forEach(key => {
                if (reasons[key]) {
                    filtered[key] = reasons[key];
                    allKeys.delete(key);
                }
            });
            return filtered;
        };

        const overview = filterReasons(overviewKeys);
        const customerFit = filterReasons(customerFitKeys);
        const siteType = filterReasons(siteTypeKeys);
        const seo = filterReasons(seoKeys);
        const performance = filterReasons(performanceKeys);
        const urls = filterReasons(urlsKeys);
        const technical = filterReasons(technicalKeys);
        const other = filterReasons(otherKeys);
        const gatekeeper = filterReasons(gatekeeperKeys);

        // Add any remaining unmatched keys to "other"
        const unmatched = {};
        allKeys.forEach(key => {
            if (reasons[key]) {
                unmatched[key] = reasons[key];
            }
        });

        return {
            overview,
            customerFit,
            siteType,
            seo,
            performance,
            urls,
            technical,
            other: { ...other, ...unmatched },
            gatekeeper
        };
    }, [websiteData.reasons]);

    // Handle screenshot expansion
    useEffect(() => {
        if (expandedScreenshotType === 'desktop' && !websiteScreenshot) {
            setExpandedScreenshotType(null);
        }

        if (expandedScreenshotType === 'mobile' && !websiteMobileScreenshot) {
            setExpandedScreenshotType(null);
        }
    }, [expandedScreenshotType, websiteScreenshot, websiteMobileScreenshot]);

    // Set default tab based on funnel_status
    useEffect(() => {
        const status = websiteData.website?.funnel_status;
        const prospectStates = ["prospect", "crawled", "contact_extracted", "captured", "analyzed", "ready_for_outreach"];
        if (prospectStates.includes(status)) {
            setSelectedTab(1);
        } else if (status === "qualified") {
            setSelectedTab(2);
        }
    }, [websiteData.website?.funnel_status]);

    // Fake qualified evaluation data
    const fakeQualifiedEvaluation = React.useMemo(() => {
        return {
            passed: true,
            checks: {
                contact_quality: {
                    passed: true,
                    reason: 'High quality contacts found with scores above threshold'
                },
                website_structure: {
                    passed: true,
                    reason: 'Well-structured website with clear navigation'
                },
                content_quality: {
                    passed: true,
                    reason: 'Relevant and comprehensive content available'
                },
                technical_quality: {
                    passed: true,
                    reason: 'Good technical performance and accessibility'
                }
            },
            details: {
                total_contacts: 8,
                good_contacts_count: 6,
                total_pages_crawled: 45,
                max_pages_reached: false,
                contact_scores: [7, 8, 6, 7, 9, 6, 5, 4]
            }
        };
    }, []);

    // Check if there's a suitable contact (score >= 5)
    const hasSuitableContact = React.useMemo(() => {
        if (!websiteData.contacts || !websiteData.contacts.contacts || !Array.isArray(websiteData.contacts.contacts)) {
            return false;
        }
        return websiteData.contacts.contacts.some(contact => {
            const score = contact.overall_score;
            return score !== undefined && score !== null && score >= 5 && !contact.is_nonsensical;
        });
    }, [websiteData.contacts]);

    // Determine if Pass button should be disabled
    const isPassButtonDisabled = websiteData.crawlStatistics?.max_pages_reached || !hasSuitableContact;

    // Helper functions to check if tabs have content
    const hasOverviewContent = React.useMemo(() => {
        return (websiteData.standardAnswers && websiteData.standardAnswers.length > 0) ||
               (websiteData.reasons && Object.keys(websiteData.reasons).length > 0);
    }, [websiteData.standardAnswers, websiteData.reasons]);

    const hasDesignSemanticContent = React.useMemo(() => {
        return websiteData.designSemantic && websiteData.designSemantic.result;
    }, [websiteData.designSemantic]);

    const hasContactsContent = React.useMemo(() => {
        return websiteData.contacts && typeof websiteData.contacts === 'object' &&
               websiteData.contacts.contacts && Array.isArray(websiteData.contacts.contacts) &&
               websiteData.contacts.contacts.length > 0;
    }, [websiteData.contacts]);

    const hasCustomerFitContent = React.useMemo(() => {
        const { customerFitData } = websiteData;
        return customerFitData && (
            customerFitData.hasCustomerFitRating ||
            customerFitData.hasCustomerFitConfidence ||
            customerFitData.hasCustomerFitSignals ||
            customerFitData.hasCustomerFitRecommendedMotion ||
            (filteredReasons.customerFit && Object.keys(filteredReasons.customerFit).length > 0)
        );
    }, [websiteData.customerFitData, filteredReasons.customerFit]);

    const hasSiteTypeContent = React.useMemo(() => {
        const { siteTypeData } = websiteData;
        return siteTypeData && (
            siteTypeData.type ||
            siteTypeData.subType ||
            siteTypeData.confidence ||
            siteTypeData.primaryCta ||
            siteTypeData.notes ||
            (Array.isArray(siteTypeData.signals) ? siteTypeData.signals.length > 0 : siteTypeData.signals) ||
            (filteredReasons.siteType && Object.keys(filteredReasons.siteType).length > 0)
        );
    }, [websiteData.siteTypeData, filteredReasons.siteType]);

    const hasSEOContent = React.useMemo(() => {
        return (websiteData.seoAnswers && websiteData.seoAnswers.length > 0) ||
               (filteredReasons.seo && Object.keys(filteredReasons.seo).length > 0);
    }, [websiteData.seoAnswers, filteredReasons.seo]);

    const hasContentTabContent = React.useMemo(() => {
        return (websiteData.imagesData && websiteData.imagesData.images && websiteData.imagesData.images.length > 0) ||
               (websiteData.videosData && websiteData.videosData.videos && websiteData.videosData.videos.length > 0) ||
               (websiteData.filesData && websiteData.filesData.files && websiteData.filesData.files.length > 0) ||
               (websiteData.formsData && websiteData.formsData.length > 0) ||
               (websiteData.contactFormsData && websiteData.contactFormsData.length > 0) ||
               (websiteData.urlData?.contactFormUrls && websiteData.urlData.contactFormUrls.length > 0) ||
               (websiteData.urlData?.blogUrls && websiteData.urlData.blogUrls.length > 0) ||
               (websiteData.urlData?.productGalleryUrls && websiteData.urlData.productGalleryUrls.length > 0) ||
               (websiteData.urlData?.googleMapsUrls && websiteData.urlData.googleMapsUrls.length > 0) ||
               (websiteData.urlData?.thirdPartyIntegrationsUrls && Object.keys(websiteData.urlData.thirdPartyIntegrationsUrls).length > 0) ||
               (websiteData.navigationStructure && typeof websiteData.navigationStructure === 'object') ||
               (filteredReasons.urls && Object.keys(filteredReasons.urls).length > 0);
    }, [websiteData.imagesData, websiteData.videosData, websiteData.filesData, websiteData.formsData, websiteData.contactFormsData, websiteData.urlData, websiteData.navigationStructure, filteredReasons.urls]);

    const hasPagesContent = React.useMemo(() => {
        return websiteData.pagesMetadata && Array.isArray(websiteData.pagesMetadata) && websiteData.pagesMetadata.length > 0;
    }, [websiteData.pagesMetadata]);

    const hasCssContent = React.useMemo(() => {
        const css = websiteData.cssData;
        if (!css) return false;
        const {
            inline_css,
            css_urls,
            css_variables,
            colors,
            typography,
            spacing,
            effects,
            layout,
            components
        } = css;

        return Boolean(
            (inline_css && inline_css.length > 0) ||
            (css_urls && css_urls.length > 0) ||
            (css_variables && Object.keys(css_variables).length > 0) ||
            (colors && ((colors.values && colors.values.length > 0) || (colors.frequency && Object.keys(colors.frequency).length > 0))) ||
            (typography && Object.keys(typography).length > 0) ||
            (spacing && Object.keys(spacing).length > 0) ||
            (effects && Object.keys(effects).length > 0) ||
            (layout && Object.keys(layout).length > 0) ||
            (components && Object.keys(components).length > 0)
        );
    }, [websiteData.cssData]);

    const hasTechnicalContent = React.useMemo(() => {
        return lighthouseReport ||
               (websiteData.networkSummary && typeof websiteData.networkSummary === 'object') ||
               (websiteData.performanceMetrics && typeof websiteData.performanceMetrics === 'object') ||
               (websiteData.dnsAnswers && websiteData.dnsAnswers.length > 0) ||
               (websiteData.trackingData) ||
               (websiteData.consoleLogs && websiteData.consoleLogs.length > 0) ||
               hasCssContent ||
               (filteredReasons.performance && Object.keys(filteredReasons.performance).length > 0) ||
               (filteredReasons.technical && Object.keys(filteredReasons.technical).length > 0);
    }, [lighthouseReport, websiteData.networkSummary, websiteData.performanceMetrics, websiteData.dnsAnswers, websiteData.trackingData, websiteData.consoleLogs, hasCssContent, filteredReasons.performance, filteredReasons.technical]);

    const hasCrawlStatisticsContent = React.useMemo(() => {
        return websiteData.crawlStatistics && typeof websiteData.crawlStatistics === 'object';
    }, [websiteData.crawlStatistics]);

    const hasOtherContent = React.useMemo(() => {
        return (websiteData.donationAnswers && websiteData.donationAnswers.length > 0) ||
               (websiteData.longTextAnswers && websiteData.longTextAnswers.length > 0) ||
               (filteredReasons.other && Object.keys(filteredReasons.other).length > 0);
    }, [websiteData.donationAnswers, websiteData.longTextAnswers, filteredReasons.other]);

    const hasConceptContent = React.useMemo(() => {
        return websiteData.conceptBlueprint || websiteData.proposedPages;
    }, [websiteData.conceptBlueprint, websiteData.proposed_pages]);

    // Calculate visible tab count for bounds checking
    const visibleTabCount = React.useMemo(() => {
        let count = 0;
        if (hasOverviewContent) count++;
        if (hasContactsContent) count++;
        if (hasCustomerFitContent) count++;
        if (hasSiteTypeContent) count++;
        if (hasSEOContent) count++;
        if (hasContentTabContent) count++;
        if (hasPagesContent) count++;
        if (hasTechnicalContent) count++;
        if (hasCrawlStatisticsContent) count++;
        if (hasOtherContent) count++;
        if (hasDesignSemanticContent) count++;
        if (websiteData.socialMediaData && websiteData.socialMediaData.value) count++;
        if (websiteData.gatekeeperData && typeof websiteData.gatekeeperData === 'object') count++;
        if (websiteData.structure || websiteData.navigation) count++;
        if (hasConceptContent) count++;
        return count;
    }, [hasOverviewContent, hasContactsContent, hasCustomerFitContent, hasSiteTypeContent, hasSEOContent, hasContentTabContent, hasPagesContent, hasTechnicalContent, hasCrawlStatisticsContent, hasOtherContent, hasDesignSemanticContent, hasConceptContent, websiteData.socialMediaData, websiteData.gatekeeperData, websiteData.structure, websiteData.navigation]);

    // Adjust selected tab if it's out of bounds
    useEffect(() => {
        if (visibleTabCount > 0 && selectedDetailsTab >= visibleTabCount) {
            setSelectedDetailsTab(0);
        }
    }, [visibleTabCount, selectedDetailsTab]);

    // Load moodboard HTML when modal opens
    useEffect(() => {
        if (!isMoodboardModalOpen || !selectedWebsiteHash || !websocketClient) {
            return;
        }

        setIsMoodboardLoading(true);
        setMoodboardError('');
        setMoodboardHtml('');

        websocketClient.waitForSocketAndSend({
            action: "getWebsiteMoodboard",
            options: {
                url_hash: selectedWebsiteHash
            }
        }, (response) => {
            setIsMoodboardLoading(false);

            if (!response) {
                setMoodboardError('Unable to load moodboard for this website.');
                return;
            }

            if (response.error) {
                setMoodboardError(typeof response.error === 'string' ? response.error : 'Unable to load moodboard for this website.');
                return;
            }

            let html = '';

            if (typeof response === 'string') {
                html = response;
            } else if (typeof response.html === 'string') {
                html = response.html;
            } else if (typeof response.moodboard_html === 'string') {
                html = response.moodboard_html;
            } else if (typeof response.page === 'string') {
                html = response.page;
            }

            if (!html) {
                setMoodboardError('Moodboard response did not contain HTML content.');
                return;
            }

            setMoodboardHtml(html);
        });
    }, [isMoodboardModalOpen, selectedWebsiteHash, websocketClient]);

    // Load CMS sales pitch (full HTML + optional structured content) when modal opens
    useEffect(() => {
        if (!isSalesPitchModalOpen || !selectedWebsiteHash || !websocketClient) {
            return;
        }

        setIsSalesPitchLoading(true);
        setSalesPitchError('');
        setSalesPitchHtml('');
        setSalesPitchRpcPitchContent(null);

        websocketClient.waitForSocketAndSend({
            action: 'getWebsiteSalesPitch',
            options: {
                url_hash: selectedWebsiteHash,
            },
        }, (response) => {
            setIsSalesPitchLoading(false);

            if (!response) {
                setSalesPitchError('Unable to load sales pitch for this website.');
                return;
            }

            if (typeof response.error === 'string' && response.error.trim()) {
                setSalesPitchError(response.error.trim());
                return;
            }

            if (response.status === 'error') {
                const msg =
                    typeof response.message === 'string' && response.message.trim()
                        ? response.message.trim()
                        : 'Sales pitch could not be loaded.';
                setSalesPitchError(msg);
                return;
            }

            const html =
                typeof response.html === 'string' && response.html.length > 0 ? response.html : null;
            let pitchContent = null;
            if (response.pitch_content && typeof response.pitch_content === 'object') {
                pitchContent = response.pitch_content;
            }

            if (!html && !pitchContent) {
                setSalesPitchError('Sales pitch response did not contain HTML or structured content.');
                return;
            }

            if (html) {
                setSalesPitchHtml(html);
            }
            if (pitchContent) {
                setSalesPitchRpcPitchContent(pitchContent);
            }
        });
    }, [isSalesPitchModalOpen, selectedWebsiteHash, websocketClient]);

    const salesPitchGeneratedLabel = React.useMemo(() => {
        const raw = websiteData.salesPitchContent?.generated_at;
        if (raw == null || raw === '') {
            return null;
        }
        try {
            const d = new Date(raw);
            return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString();
        } catch {
            return String(raw);
        }
    }, [websiteData.salesPitchContent]);

    const salesPitchHookSnippet = React.useMemo(() => {
        const h = websiteData.salesPitchContent?.pitch?.hook_headline;
        if (typeof h !== 'string' || !h.trim()) {
            return null;
        }
        const t = h.trim();
        return t.length > 100 ? `${t.slice(0, 100)}…` : t;
    }, [websiteData.salesPitchContent]);

    const openSalesToolsButtonStyle = {
        backgroundColor: '#ffffff',
        color: '#111827',
        borderRadius: '999px',
        border: '1px solid rgba(148, 163, 184, 0.8)',
        padding: '8px 16px',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 4px 10px rgba(15, 23, 42, 0.08)',
        backgroundImage: 'linear-gradient(to right, #f9fafb, #ffffff)',
    };

    if (!websiteData.website) {
        return null;
    }

    const tableHeaderStyle = {
        textAlign: 'left',
        padding: '12px 16px',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: '#9a9a9a',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        backgroundColor: '#1f1f1f'
    };

    const tableCellStyle = {
        padding: '12px 16px',
        fontSize: '13px',
        color: '#dcdcdc',
        borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
    };

    return (
        <div>
            <h3>{websiteData.mainUrl}</h3>
            {websiteData.evaluationMessage && (
                <div
                    role="alert"
                    style={{
                        marginTop: '8px',
                        marginBottom: '15px',
                        padding: '12px 16px',
                        backgroundColor: 'rgba(239, 68, 68, 0.12)',
                        border: '1px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '8px',
                        color: '#fca5a5',
                        fontSize: '14px',
                        lineHeight: 1.5,
                    }}
                >
                    <strong style={{ color: '#f87171' }}>Evaluation failed:</strong>{' '}
                    {websiteData.evaluationMessage}
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px', flexWrap: 'wrap' }}>
                {websiteData.website?.funnel_status && (
                    <span
                        style={{
                            backgroundColor: FUNNEL_STATUS_COLORS[websiteData.website.funnel_status] || '#60A5FA',
                            color: '#ffffff',
                            padding: '4px 12px',
                            borderRadius: '9999px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            display: 'inline-block',
                        }}
                    >
                        {FUNNEL_STATUS_LABELS[websiteData.website.funnel_status] || websiteData.website.funnel_status}
                    </span>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#9ca3af' }}>
                    <span>Set status:</span>
                    <select
                        value={websiteData.website?.funnel_status || ''}
                        onChange={(e) => {
                            const newStatus = e.target.value;
                            if (newStatus && selectedWebsiteHash && onPromoteWebsite) {
                                onPromoteWebsite(selectedWebsiteHash, newStatus);
                            }
                        }}
                        style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: '1px solid #475569',
                            backgroundColor: '#1e293b',
                            color: '#e2e8f0',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            minWidth: '140px',
                        }}
                    >
                        {PIPELINE_ORDER.map((status) => (
                            <option key={status} value={status}>
                                {FUNNEL_STATUS_LABELS[status] || status}
                            </option>
                        ))}
                    </select>
                </label>
                <button
                    type="button"
                    title="Open the generated CMS sales pitch. Generate via batch job cms.WebsiteProspectPitch or cms/build_sales_pitch."
                    onClick={() => setIsSalesPitchModalOpen(true)}
                    style={{
                        ...openSalesToolsButtonStyle,
                        marginLeft: '4px',
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundImage = 'linear-gradient(to right, #f3f4f6, #ffffff)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundImage = 'linear-gradient(to right, #f9fafb, #ffffff)';
                    }}
                >
                    <span style={{ fontSize: '16px' }}>📣</span>
                    <span>Sales pitch</span>
                </button>
                {websiteData.salesPitchContent && (
                    <span
                        style={{
                            fontSize: '12px',
                            color: '#94a3b8',
                            maxWidth: '420px',
                            lineHeight: 1.4,
                        }}
                        title={
                            salesPitchHookSnippet
                                ? websiteData.salesPitchContent?.pitch?.hook_headline
                                : undefined
                        }
                    >
                        <span
                            style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '999px',
                                backgroundColor: 'rgba(52, 211, 153, 0.15)',
                                color: '#6ee7b7',
                                fontWeight: 600,
                                marginRight: '8px',
                            }}
                        >
                            Pitch available
                        </span>
                        {salesPitchGeneratedLabel && <>Last generated {salesPitchGeneratedLabel}</>}
                        {salesPitchHookSnippet && (
                            <>
                                {salesPitchGeneratedLabel ? ' · ' : null}
                                {salesPitchHookSnippet}
                            </>
                        )}
                    </span>
                )}
            </div>
            <hr />
            {/* Manual Actions Section */}
            <ManualActionsSection
                manualActions={websiteData.manualActions}
                onActionClick={(action) => {
                    setSelectedAction(action);
                    if (action.action === 'add_contact') {
                        setIsAddContactModalOpen(true);
                    }
                }}
            />
            
            {/* Website Audit Logs Button */}
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-start' }}>
                <button
                    onClick={() => setIsWebsiteAuditLogsModalOpen(true)}
                    style={{
                        backgroundColor: '#6366f1',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
                        transition: 'all 0.2s ease',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#4f46e5';
                        e.target.style.boxShadow = '0 6px 14px rgba(99, 102, 241, 0.45)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#6366f1';
                        e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.35)';
                    }}
                >
                    📋 View Audit Logs
                </button>
            </div>
            <Tabs selectedIndex={selectedTab} onSelect={setSelectedTab}>
                <TabList>
                    <Tab>Audit</Tab>
                    {websiteData.website && websiteData.website.funnel_status !== "discarded" && websiteData.website.funnel_status !== "new" && (
                        <Tab>Prospect</Tab>
                    )}
                    {websiteData.website && websiteData.website.funnel_status === "qualified" && (
                        <Tab>Qualified</Tab>
                    )}
                </TabList>
                <TabPanel>
                    <div style={{ padding: '20px' }}>
                        {/* Main URL and Screenshot */}
                        <div style={{ 
                            marginBottom: '30px',
                            display: 'flex',
                            gap: '20px',
                            alignItems: 'flex-start'
                        }}>
                            <WebsiteHeader
                                mainUrl={websiteData.mainUrl}
                                websiteScreenshot={websiteScreenshot}
                                websiteMobileScreenshot={websiteMobileScreenshot}
                                onScreenshotClick={setExpandedScreenshotType}
                            />
                            
                            <div style={{ flex: 1 }}>
                                {/* Evaluation Section */}
                                <EvaluationSection
                                    evaluation={websiteData.evaluation}
                                    gatekeeperRejected={websiteData.gatekeeperRejected}
                                    gatekeeperShouldContinue={websiteData.gatekeeperShouldContinue}
                                    parsedScore={websiteData.parsedScore}
                                    scoreVisuals={websiteData.scoreVisuals}
                                    customerFitData={websiteData.customerFitData}
                                    customerFitVisuals={websiteData.customerFitVisuals}
                                    headerActions={
                                        <>
                                            <button
                                                onClick={() => {
                                                    if (selectedWebsiteHash && onPromoteWebsite) {
                                                        onPromoteWebsite(selectedWebsiteHash, 'discarded');
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: '#ef4444',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '8px 16px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.35)',
                                                    transition: 'all 0.2s ease',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    minWidth: '90px',
                                                    justifyContent: 'center'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#dc2626';
                                                    e.target.style.boxShadow = '0 6px 14px rgba(239, 68, 68, 0.45)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = '#ef4444';
                                                    e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.35)';
                                                }}
                                            >
                                                Discard
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (selectedWebsiteHash && onPromoteWebsite) {
                                                        onPromoteWebsite(selectedWebsiteHash, 'prospect');
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: '#10b981',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '8px 16px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
                                                    transition: 'all 0.2s ease',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    minWidth: '90px',
                                                    justifyContent: 'center'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#059669';
                                                    e.target.style.boxShadow = '0 6px 14px rgba(16, 185, 129, 0.45)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = '#10b981';
                                                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.35)';
                                                }}
                                            >
                                                Pass
                                            </button>
                                        </>
                                    }
                                />

                                {/* Top Gatekeeper Summary */}
                                {websiteData.showTopGatekeeperSummary && (
                                    <div style={{ marginBottom: '30px' }}>
                                        <h4 style={{ marginBottom: '15px', color: '#e0e0e0', fontWeight: '600' }}>Gatekeeper Review</h4>
                                        <div
                                            style={{
                                                background: websiteData.gatekeeperStatus.background,
                                                border: `1px solid ${websiteData.gatekeeperStatus.border}`,
                                                borderRadius: '16px',
                                                padding: '20px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '16px'
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <span style={{
                                                    color: '#9ca3af',
                                                    fontSize: '12px',
                                                    letterSpacing: '0.08em',
                                                    textTransform: 'uppercase',
                                                    fontWeight: 600
                                                }}>
                                                    Status
                                                </span>
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        alignSelf: 'flex-start',
                                                        padding: '6px 16px',
                                                        borderRadius: '999px',
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        letterSpacing: '0.4px',
                                                        backgroundColor: websiteData.gatekeeperStatus.color,
                                                        color: '#0b0b0b'
                                                    }}
                                                >
                                                    {websiteData.gatekeeperStatus.label}
                                                </span>
                                            </div>

                                            {websiteData.gatekeeperCategory && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <span style={{
                                                        color: '#9ca3af',
                                                        fontSize: '12px',
                                                        letterSpacing: '0.08em',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 600
                                                    }}>
                                                        Category
                                                    </span>
                                                    <span style={{
                                                        fontSize: '18px',
                                                        fontWeight: 600,
                                                        color: '#f9fafb'
                                                    }}>
                                                        {formatValue(websiteData.gatekeeperCategory)}
                                                    </span>
                                                </div>
                                            )}

                                            {websiteData.gatekeeperConfidence !== null && websiteData.gatekeeperConfidence !== undefined && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <span style={{
                                                        color: '#9ca3af',
                                                        fontSize: '12px',
                                                        letterSpacing: '0.08em',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 600
                                                    }}>
                                                        Confidence
                                                    </span>
                                                    <span style={{
                                                        fontSize: '18px',
                                                        fontWeight: 600,
                                                        color: '#f9fafb'
                                                    }}>
                                                        {formatConfidence(websiteData.gatekeeperConfidence)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Website Evaluation Section */}
                        <WebsiteEvaluationSection
                            websiteEvaluation={websiteData.evaluation}
                        />
                        
                        {/* Screenshot Modal - stays in tab since it's used by WebsiteHeader */}
                        <ScreenshotModal
                            isOpen={Boolean(expandedScreenshotType && (expandedScreenshotType === 'mobile' ? websiteMobileScreenshot : websiteScreenshot))}
                            onClose={() => setExpandedScreenshotType(null)}
                            websiteScreenshot={expandedScreenshotType === 'mobile' ? websiteMobileScreenshot : websiteScreenshot}
                            mainUrl={websiteData.mainUrl}
                            screenshotLabel={expandedScreenshotType === 'mobile' ? 'Mobile' : expandedScreenshotType === 'desktop' ? 'Desktop' : ''}
                        />
                    </div>
                </TabPanel>
                {websiteData.website && websiteData.website.funnel_status !== "discarded" && websiteData.website.funnel_status !== "new" && (
                        <TabPanel>
                        {/* Prospect section */}
                        <div style={{ padding: '20px' }}>
                            {/* Pipeline Progress */}
                            <PipelineProgressSection 
                                funnelStatus={websiteData.website?.funnel_status}
                                prospectEvaluation={websiteData.prospectEvaluation}
                                evaluationMessage={websiteData.evaluationMessage}
                            />
                            {/* Main URL and Screenshot */}
                            <div style={{ 
                                marginBottom: '30px',
                                display: 'flex',
                                gap: '20px',
                                alignItems: 'flex-start'
                            }}>
                            <WebsiteHeader
                                mainUrl={websiteData.mainUrl}
                                websiteScreenshot={websiteScreenshot}
                                websiteMobileScreenshot={websiteMobileScreenshot}
                                onScreenshotClick={setExpandedScreenshotType}
                            />
                            
                            <div style={{ flex: 1 }}>
                                {/* Prospect Evaluation Summary */}
                                <ProspectEvaluationSummary 
                                    prospectEvaluation={websiteData.prospectEvaluation}
                                    headerActions={
                                        <>
                                            <button
                                                onClick={() => {
                                                    if (selectedWebsiteHash && onPromoteWebsite) {
                                                        onPromoteWebsite(selectedWebsiteHash, 'discarded');
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: '#ef4444',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '8px 16px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.35)',
                                                    transition: 'all 0.2s ease',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    minWidth: '90px',
                                                    justifyContent: 'center'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#dc2626';
                                                    e.target.style.boxShadow = '0 6px 14px rgba(239, 68, 68, 0.45)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = '#ef4444';
                                                    e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.35)';
                                                }}
                                            >
                                                Discard
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (selectedWebsiteHash && onPromoteWebsite && !isPassButtonDisabled) {
                                                        onPromoteWebsite(selectedWebsiteHash, 'qualified');
                                                    }
                                                }}
                                                disabled={isPassButtonDisabled}
                                                style={{
                                                    backgroundColor: isPassButtonDisabled ? '#4a4a4a' : '#10b981',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '8px 16px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: isPassButtonDisabled ? 'not-allowed' : 'pointer',
                                                    boxShadow: isPassButtonDisabled ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.35)',
                                                    transition: 'all 0.2s ease',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    minWidth: '90px',
                                                    justifyContent: 'center',
                                                    opacity: isPassButtonDisabled ? 0.6 : 1
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isPassButtonDisabled) {
                                                        e.target.style.backgroundColor = '#059669';
                                                        e.target.style.boxShadow = '0 6px 14px rgba(16, 185, 129, 0.45)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isPassButtonDisabled) {
                                                        e.target.style.backgroundColor = '#10b981';
                                                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.35)';
                                                    }
                                                }}
                                            >
                                                Pass
                                            </button>
                                        </>
                                    }
                                />
                            </div>
                        </div>

                        {/* Prospect Evaluation Section */}
                        <ProspectEvaluationSection
                            prospectEvaluation={websiteData.prospectEvaluation}
                        />
                        </div>
                    </TabPanel>
                    )}
                    {websiteData.website && websiteData.website.funnel_status === "qualified" && (
                        <TabPanel>
                        {/* Qualified section */}
                        <div style={{ padding: '20px' }}>
                            {/* Main URL and Screenshot */}
                            <div style={{ 
                                marginBottom: '30px',
                                display: 'flex',
                                gap: '20px',
                                alignItems: 'flex-start'
                            }}>
                            <WebsiteHeader
                                mainUrl={websiteData.mainUrl}
                                websiteScreenshot={websiteScreenshot}
                                websiteMobileScreenshot={websiteMobileScreenshot}
                                onScreenshotClick={setExpandedScreenshotType}
                            />
                            
                            <div style={{ flex: 1 }}>
                                {/* Qualified Evaluation Summary */}
                                <QualifiedEvaluationSummary 
                                    qualifiedEvaluation={fakeQualifiedEvaluation}
                                    headerActions={
                                        <button
                                            onClick={() => {
                                                if (selectedWebsiteHash && onPromoteWebsite) {
                                                    onPromoteWebsite(selectedWebsiteHash, 'discarded');
                                                }
                                            }}
                                            style={{
                                                backgroundColor: '#ef4444',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '6px',
                                                padding: '8px 16px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.35)',
                                                transition: 'all 0.2s ease',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                minWidth: '90px',
                                                justifyContent: 'center'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = '#dc2626';
                                                e.target.style.boxShadow = '0 6px 14px rgba(239, 68, 68, 0.45)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = '#ef4444';
                                                e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.35)';
                                            }}
                                        >
                                            Discard
                                        </button>
                                    }
                                />
                            </div>
                        </div>

                        {/* Qualified Evaluation Section */}
                        <QualifiedEvaluationSection
                            qualifiedEvaluation={fakeQualifiedEvaluation}
                        />
                        </div>
                    </TabPanel>
                    )}
                
               
            </Tabs>
            
            {/* Section Components - Organized in tabs */}
            <div style={{ padding: '20px' }}>
                <Tabs selectedIndex={selectedDetailsTab} onSelect={setSelectedDetailsTab}>
                    <TabList>
                        {hasOverviewContent && <Tab>Overview</Tab>}
                        {hasDesignSemanticContent && <Tab>Design</Tab>}
                        {hasContactsContent && <Tab>Contacts</Tab>}
                        {hasCustomerFitContent && <Tab>Customer Fit</Tab>}
                        {hasSiteTypeContent && <Tab>Site Type</Tab>}
                        {hasSEOContent && <Tab>SEO</Tab>}
                        {hasContentTabContent && <Tab>Content</Tab>}
                        {hasPagesContent && <Tab>Pages</Tab>}
                        {hasTechnicalContent && <Tab>Technical</Tab>}
                        {hasCrawlStatisticsContent && <Tab>Crawl Statistics</Tab>}
                        {hasOtherContent && <Tab>Other</Tab>}
                        {websiteData.socialMediaData && websiteData.socialMediaData.value && (
                            <Tab>Social Media</Tab>
                        )}
                        {websiteData.gatekeeperData && typeof websiteData.gatekeeperData === 'object' && (
                            <Tab>Gatekeeper</Tab>
                        )}
                        {(websiteData.structure || websiteData.navigation) && (
                            <Tab>Proposed Structure</Tab>
                        )}
                        {hasConceptContent && (
                            <Tab>Concept</Tab>
                        )}
                    </TabList>

                    {hasOverviewContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <AnswersSection 
                                    standardAnswers={websiteData.standardAnswers}
                                    reasons={websiteData.reasons}
                                />
                            </div>
                        </TabPanel>
                    )}

                    {hasDesignSemanticContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <div
                                    style={{
                                        marginBottom: '16px',
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                    }}
                                >
                                    <button
                                        type="button"
                                        title="Open the generated CMS sales pitch (batch: cms.WebsiteProspectPitch)."
                                        onClick={() => setIsSalesPitchModalOpen(true)}
                                        style={openSalesToolsButtonStyle}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundImage =
                                                'linear-gradient(to right, #f3f4f6, #ffffff)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundImage =
                                                'linear-gradient(to right, #f9fafb, #ffffff)';
                                        }}
                                    >
                                        <span style={{ fontSize: '16px' }}>📣</span>
                                        <span>Sales pitch</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsMoodboardModalOpen(true)}
                                        style={openSalesToolsButtonStyle}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundImage =
                                                'linear-gradient(to right, #f3f4f6, #ffffff)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundImage =
                                                'linear-gradient(to right, #f9fafb, #ffffff)';
                                        }}
                                    >
                                        <span style={{ fontSize: '16px' }}>🎨</span>
                                        <span>Open Moodboard</span>
                                    </button>
                                </div>
                                <DesignSemanticSection designSemantic={websiteData.designSemantic} />
                            </div>
                        </TabPanel>
                    )}

                    {hasContactsContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <ContactsSection 
                                    contacts={websiteData.contacts} 
                                    onViewContacts={() => setIsContactsModalOpen(true)}
                                />
                                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-start' }}>
                                    <button
                                        onClick={() => {
                                            setSelectedAction({ action: 'add_contact' });
                                            setIsAddContactModalOpen(true);
                                        }}
                                        style={{
                                            backgroundColor: '#10b981',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '10px 20px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
                                            transition: 'all 0.2s ease',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#059669';
                                            e.target.style.boxShadow = '0 6px 14px rgba(16, 185, 129, 0.45)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = '#10b981';
                                            e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.35)';
                                        }}
                                    >
                                        + Add Contact
                                    </button>
                                </div>
                            </div>
                        </TabPanel>
                    )}

                    {hasCustomerFitContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <CustomerFitSection
                                    customerFitData={websiteData.customerFitData}
                                    customerFitSignals={websiteData.customerFitSignals}
                                    customerFitVisuals={websiteData.customerFitVisuals}
                                />
                                <ReasonsSection reasons={filteredReasons.customerFit} />
                            </div>
                        </TabPanel>
                    )}

                    {hasSiteTypeContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <SiteTypeSection siteTypeData={websiteData.siteTypeData} />
                                <ReasonsSection reasons={filteredReasons.siteType} />
                            </div>
                        </TabPanel>
                    )}

                    {hasSEOContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <SEOSection seoAnswers={websiteData.seoAnswers} />
                                <ReasonsSection reasons={filteredReasons.seo} />
                            </div>
                        </TabPanel>
                    )}

                    {hasContentTabContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <ImagesSection 
                                    imagesData={websiteData.imagesData} 
                                    onViewDetails={() => setIsImagesModalOpen(true)}
                                />

                                <VideosSection 
                                    videosData={websiteData.videosData} 
                                    onViewDetails={() => setIsVideosModalOpen(true)}
                                />

                                <FilesSection 
                                    filesData={websiteData.filesData} 
                                    onViewDetails={() => setIsFilesModalOpen(true)}
                                />

                                <NavigationStructureSection navigationStructure={websiteData.navigationStructure} />

                                <FormsSection 
                                    forms={websiteData.formsData}
                                    onViewForm={(form) => {
                                        setSelectedForm(form);
                                        setIsFormModalOpen(true);
                                    }}
                                />
                                <BlogUrlsSection urls={websiteData.urlData?.blogUrls} />
                                <ProductGalleryUrlsSection urls={websiteData.urlData?.productGalleryUrls} />
                                <GoogleMapsUrlsSection urls={websiteData.urlData?.googleMapsUrls} />
                                <ThirdPartyIntegrationsUrlsSection integrationsUrls={websiteData.urlData?.thirdPartyIntegrationsUrls} />
                                <ReasonsSection reasons={filteredReasons.urls} />
                            </div>
                        </TabPanel>
                    )}

                    {hasPagesContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <PagesSection 
                                    pagesMetadata={websiteData.pagesMetadata}
                                    urlData={websiteData.urlData}
                                    imagesData={websiteData.imagesData}
                                    videosData={websiteData.videosData}
                                    filesData={websiteData.filesData}
                                    socialMediaData={websiteData.socialMediaData}
                                    websiteGraph={websiteData.websiteGraph}
                                    formsData={websiteData.formsData}
                                    contactFormsData={websiteData.contactFormsData}
                                    pageFeatures={websiteData.pageFeatures}
                                    onViewImages={(images) => {
                                        setFilteredImagesForModal({ images });
                                        setIsImagesModalOpen(true);
                                    }}
                                    onViewVideos={(videos) => {
                                        setFilteredVideosForModal({ videos });
                                        setIsVideosModalOpen(true);
                                    }}
                                    onViewFiles={(files) => {
                                        setFilteredFilesForModal({ files });
                                        setIsFilesModalOpen(true);
                                    }}
                                    onViewForm={(form) => {
                                        setSelectedForm(form);
                                        setIsFormModalOpen(true);
                                    }}
                                />
                            </div>
                        </TabPanel>
                    )}

                    {hasTechnicalContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <LighthouseSection
                                    lighthouseReport={lighthouseReport}
                                    onViewReport={() => setIsLighthouseModalOpen(true)}
                                />

                                <NetworkSummarySection
                                    networkSummary={websiteData.networkSummary}
                                    onViewDetails={() => setIsNetworkModalOpen(true)}
                                />

                                <PerformanceMetricsSection
                                    performanceMetrics={websiteData.performanceMetrics}
                                    previewCoreWebVitals={previewCoreWebVitals}
                                    defaultMetricsDisplay={websiteData.defaultMetricsDisplay}
                                    hasAdditionalMetrics={websiteData.hasAdditionalMetrics}
                                    metricsList={websiteData.metricsList}
                                    defaultMetrics={websiteData.defaultMetrics}
                                    onViewDetails={() => setIsPerformanceModalOpen(true)}
                                />

                                <CSSSection cssData={websiteData.cssData} />

                                <DNSSection dnsAnswers={websiteData.dnsAnswers} />

                                <TrackingSection trackingData={websiteData.trackingData} />

                                <ConsoleLogsSection
                                    consoleLogs={websiteData.consoleLogs}
                                    consoleLogLevelSummary={websiteData.consoleLogLevelSummary}
                                    onViewAll={() => setIsConsoleLogsModalOpen(true)}
                                />

                                <ReasonsSection reasons={{ ...filteredReasons.performance, ...filteredReasons.technical }} />
                            </div>
                        </TabPanel>
                    )}

                    {hasCrawlStatisticsContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <CrawlStatisticsSection 
                                    crawlStatistics={websiteData.crawlStatistics}
                                    onViewWebsiteGraph={() => setIsWebsiteGraphModalOpen(true)}
                                    hasWebsiteGraph={!!websiteData.websiteGraph}
                                />
                            </div>
                        </TabPanel>
                    )}

                    {hasOtherContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <DonationsSection donationAnswers={websiteData.donationAnswers} />

                                <LongTextAnswersSection longTextAnswers={websiteData.longTextAnswers} />

                                <ReasonsSection reasons={filteredReasons.other} />
                            </div>
                        </TabPanel>
                    )}

                    {websiteData.socialMediaData && websiteData.socialMediaData.value && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <SocialMediaSection socialMediaData={websiteData.socialMediaData} />
                            </div>
                        </TabPanel>
                    )}

                    {websiteData.gatekeeperData && typeof websiteData.gatekeeperData === 'object' && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <GatekeeperSection
                                    showTopGatekeeperSummary={false}
                                    showBottomGatekeeperStatusCard={websiteData.showBottomGatekeeperStatusCard}
                                    gatekeeperStatus={websiteData.gatekeeperStatus}
                                    gatekeeperCategory={websiteData.gatekeeperCategory}
                                    gatekeeperConfidence={websiteData.gatekeeperConfidence}
                                    gatekeeperReason={websiteData.gatekeeperReason}
                                    gatekeeperSignals={websiteData.gatekeeperSignals}
                                    gatekeeperRejected={websiteData.gatekeeperRejected}
                                />
                                <ReasonsSection reasons={filteredReasons.gatekeeper} />
                            </div>
                        </TabPanel>
                    )}

                    {(websiteData.structure || websiteData.navigation) && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <ProposedStructureSection
                                    structure={websiteData.structure}
                                    navigation={websiteData.navigation}
                                />
                            </div>
                        </TabPanel>
                    )}

                    {hasConceptContent && (
                        <TabPanel>
                            <div style={{ padding: '20px 0' }}>
                                <ConceptSection
                                    conceptBlueprint={websiteData.conceptBlueprint}
                                    proposedPages={websiteData.proposedPages}
                                />
                            </div>
                        </TabPanel>
                    )}
                </Tabs>
            </div>
            
            {/* Modals - Outside Tabs so they work from any tab */}
            <LighthouseModal
                isOpen={isLighthouseModalOpen && Boolean(lighthouseReport)}
                onClose={() => setIsLighthouseModalOpen(false)}
                isReportLoading={isReportLoading}
                lighthouseReportHtml={lighthouseReportHtml}
                lighthouseReportError={lighthouseReportError}
            />

            <NetworkDetailsModal
                isOpen={isNetworkModalOpen && Boolean(websiteData.networkSummary)}
                onClose={() => setIsNetworkModalOpen(false)}
                initiatorEntries={websiteData.initiatorEntries}
                resourceEntries={websiteData.resourceEntries}
                tableHeaderStyle={tableHeaderStyle}
                tableCellStyle={tableCellStyle}
                formatValue={formatValue}
            />

            <PerformanceDetailsModal
                isOpen={isPerformanceModalOpen && Boolean(websiteData.performanceMetrics)}
                onClose={() => setIsPerformanceModalOpen(false)}
                metricsList={websiteData.metricsList}
                navigationTiming={websiteData.navigationTiming}
                formatFieldName={formatFieldName}
                formatValue={formatValue}
                tableHeaderStyle={tableHeaderStyle}
                tableCellStyle={tableCellStyle}
                coreWebVitals={websiteData.coreWebVitals}
                coreWebVitalsSummary={coreWebVitalsSummary}
                thirdPartySplit={websiteData.thirdPartySplit}
                blockingScripts={websiteData.blockingScripts}
                coverage={websiteData.coverage}
                formatDurationMs={formatDurationMs}
            />

            <ConsoleLogsModal
                isOpen={isConsoleLogsModalOpen && websiteData.consoleLogs.length > 0}
                onClose={() => setIsConsoleLogsModalOpen(false)}
                logs={websiteData.consoleLogs}
            />

            <WebsiteGraphModal
                isOpen={isWebsiteGraphModalOpen}
                onClose={() => setIsWebsiteGraphModalOpen(false)}
                websiteGraph={websiteData.websiteGraph}
            />

            <ContactsModal
                isOpen={isContactsModalOpen}
                onClose={() => setIsContactsModalOpen(false)}
                contacts={websiteData.contacts}
            />

            <ImagesModal
                isOpen={isImagesModalOpen}
                onClose={() => {
                    setIsImagesModalOpen(false);
                    setFilteredImagesForModal(null);
                }}
                imagesData={filteredImagesForModal || websiteData.imagesData}
            />

            <VideosModal
                isOpen={isVideosModalOpen}
                onClose={() => {
                    setIsVideosModalOpen(false);
                    setFilteredVideosForModal(null);
                }}
                videosData={filteredVideosForModal || websiteData.videosData}
            />

            <FilesModal
                isOpen={isFilesModalOpen}
                onClose={() => {
                    setIsFilesModalOpen(false);
                    setFilteredFilesForModal(null);
                }}
                filesData={filteredFilesForModal || websiteData.filesData}
            />

            <FormDetailsModal
                isOpen={isFormModalOpen}
                onClose={() => {
                    setIsFormModalOpen(false);
                    setSelectedForm(null);
                }}
                form={selectedForm}
            />

            <AddContactModal
                isOpen={isAddContactModalOpen}
                onClose={() => {
                    setIsAddContactModalOpen(false);
                    setSelectedAction(null);
                }}
                action={selectedAction}
                existingContacts={websiteData.contacts}
                websiteHash={selectedWebsiteHash}
                onSuccess={(response) => {
                    console.log("Contact added successfully", response);
                    // Reload website details after successful action
                    if (onReloadWebsite && selectedWebsiteHash) {
                        onReloadWebsite(selectedWebsiteHash);
                    }
                }}
            />

            <WebsiteAuditLogsModal
                isOpen={isWebsiteAuditLogsModalOpen}
                onClose={() => setIsWebsiteAuditLogsModalOpen(false)}
                logs={auditLogs}
                isLoading={auditLogsLoading}
            />

            <WebsiteMoodboardModal
                isOpen={isMoodboardModalOpen}
                onClose={() => {
                    setIsMoodboardModalOpen(false);
                    setMoodboardHtml('');
                    setMoodboardError('');
                    setIsMoodboardLoading(false);
                }}
                isLoading={isMoodboardLoading}
                html={moodboardHtml}
                error={moodboardError}
            />

            <WebsiteSalesPitchModal
                isOpen={isSalesPitchModalOpen}
                onClose={() => {
                    setIsSalesPitchModalOpen(false);
                    setSalesPitchHtml('');
                    setSalesPitchRpcPitchContent(null);
                    setSalesPitchError('');
                    setIsSalesPitchLoading(false);
                }}
                isLoading={isSalesPitchLoading}
                html={salesPitchHtml}
                pitchContent={
                    salesPitchRpcPitchContent ||
                    (!salesPitchHtml && websiteData.salesPitchContent) ||
                    null
                }
                error={salesPitchError}
            />
        </div>
       
    );
};

export default WebsiteDetails;
